import get from 'lodash/get';

import { mergeTrackingProperties, transformNamedAnswers } from './calendarCommon';
import { CALENDLY_URL_REGEX, NAMED_ANSWERS } from './constants';

import type { FormValues, Input } from '$lib/types/Form';

let calendlyScriptsEnqueued = false;

interface CalendlyConfig {
    url: string;
    prefill: {
        customAnswers: FormValues;
        [key: string]: unknown;
    };
    utm: {
        utmCampaign?: string;
        utmSource?: string;
        utmMedium?: string;
        utmContent?: string;
        utmTerm?: string;
    };
}

/**
 * Appends the phone params to the Calendly URL.
 *
 * FUN-4081: Built via `URLSearchParams` rather than string concatenation so the value is
 * percent-encoded. Phone numbers routinely start with a `+`, which Calendly would otherwise
 * decode as a space (`+31 6 123456` arriving as ` 31 6 123456`). This also keeps any query
 * params the customer already put on their Calendly link intact instead of appending a
 * second `?`.
 */
const appendPhoneParams = (calendlyUrl: string, phone: string): string => {
    try {
        const url = new URL(calendlyUrl);

        url.searchParams.set('location', phone);
        url.searchParams.set('phone_number', phone);

        return url.toString();
    } catch {
        // Should not happen: callers validate the URL before building the config.
        return calendlyUrl;
    }
};

/**
 * Calendly integration module
 * Provides utilities for loading scripts, validating URLs, extracting form data,
 * and building configuration for Calendly popup widgets
 */
export const calendly = {
    /**
     * Loads Calendly widget scripts and styles
     * Only loads once per page session
     */
    enqueueScripts: () => {
        if (calendlyScriptsEnqueued) {
            return;
        }

        const scriptTag = document.createElement('script');
        const linkTag = document.createElement('link');

        scriptTag.src = 'https://assets.calendly.com/assets/external/widget.js';
        scriptTag.type = 'text/javascript';

        linkTag.rel = 'stylesheet';
        linkTag.href = 'https://assets.calendly.com/assets/external/widget.css';

        document.getElementsByTagName('body')[0].appendChild(scriptTag);
        document.getElementsByTagName('head')[0].appendChild(linkTag);

        calendlyScriptsEnqueued = true;
    },

    /**
     * Validates if a URL matches the Calendly URL format
     */
    validateUrl: (url: string): boolean => {
        return CALENDLY_URL_REGEX.test(url);
    },

    /**
     * Transforms name field variations (fname/lname) to standard firstName/lastName format
     */
    transformNamedAnswers: (namedAnswers: FormValues): FormValues => {
        return transformNamedAnswers(namedAnswers);
    },

    /**
     * Extracts and formats form answers for Calendly API
     * - Named answers (email, name, phone) are separated for direct prefill
     * - Other answers become custom answers (a1, a2, etc.)
     * - Phone numbers are included in both named and custom answers
     * - Tracking properties are merged but limited to 10 custom answers
     */
    extractCustomAnswers: (
        inputs: Input[],
        formData: FormValues,
        allTrackingProperties: FormValues,
    ) => {
        let counter = 1;
        const customAnswers: FormValues = {};
        const namedAnswers: FormValues = {};

        inputs.forEach((input) => {
            const { name } = input;
            const value = get(formData, name, '');

            if (NAMED_ANSWERS.includes(name)) {
                namedAnswers[name] = value;

                if (name !== 'phone') {
                    // The phone field should additionally be passed as a custom answer
                    // otherwise users would need to create a "Call" type event.
                    // Now they can add a phone number as custom event also to a Meet Event
                    return;
                }
            }

            if (typeof value === 'boolean') {
                return;
            }

            customAnswers[`a${counter}`] = value;
            counter += 1;
        });

        const mergedNamedAnswers = mergeTrackingProperties({
            allTrackingProperties,
            namedAnswers,
            maxTrackingProperties: counter,
        });

        return {
            customAnswers,
            namedAnswers: calendly.transformNamedAnswers(mergedNamedAnswers),
        };
    },

    /**
     * Builds the configuration object for Calendly popup widget
     */
    buildConfig: (
        calendlyUrl: string,
        namedAnswers: FormValues,
        customAnswers: FormValues,
        trackingProperties: FormValues,
    ): CalendlyConfig => {
        const { phone } = namedAnswers;

        // PER-2085: Prefill does currently not directly support these fields, so they are added to the url
        let url = calendlyUrl;

        if (phone) {
            url = appendPhoneParams(url, String(phone));
        }

        const { utm_campaign, utm_source, utm_medium, utm_content, utm_term } = trackingProperties;

        return {
            url,
            prefill: {
                ...namedAnswers,
                customAnswers,
            },
            utm: {
                ...(utm_campaign && { utmCampaign: utm_campaign as string }),
                ...(utm_source && { utmSource: utm_source as string }),
                ...(utm_medium && { utmMedium: utm_medium as string }),
                ...(utm_content && { utmContent: utm_content as string }),
                ...(utm_term && { utmTerm: utm_term as string }),
            },
        };
    },
};
